import {AiChatSource} from '@Components/ai-chat-window/ai-chat-window.types';
import {GA4EventName, trackGA4CustomEvent} from '@Libraries/ga-events';
import type {DuplicatePostAsDraftResponse, MarketingPlannerFeedback, MarketingPlannerPost, MarketingPlannerPromptPill, MarketingPlannerProps, MarketingPlannerState} from '@Libraries/marketing-planner.library.types';
import {FAKE_DOOR_WIZARD_SOURCE} from '@Libraries/marketing-planner.library.types';
import {MY_STUFF_HASHES} from '@Libraries/my-stuff/my-stuff-library';
import {setPrefilledSchedulingDate} from '@Libraries/social-media-grid.library';
import {DAYS_IN_WEEK} from '@Utils/date.util';
import {percentEncodeQuerySpaces} from '@Utils/url.util';

// Captured once at module load so a later rewrite of the URL (SPA navigation, wizard redirects) can't erase it.
const fakeDoorSourceParam = new URLSearchParams(window.location.search).get('source');
const isFakeDoorAiChatSession = fakeDoorSourceParam === AiChatSource.MY_STUFF_MARKETING_PLANNER;
const isFakeDoorWizardSession = fakeDoorSourceParam === FAKE_DOOR_WIZARD_SOURCE;
let hasTrackedFakeDoorPostUsed = false;
let hasTrackedFakeDoorPostPublished = false;

/**
 * Returns the next calendar occurrence of the weekday/time a post was last published at,
 * strictly after `now`. Used to suggest an "ideal" repost time for evergreen content.
 */
export const getNextIdealPostTime = (lastPublishedOn: number, now: Date = new Date()): Date => {
  const lastPublished = new Date(lastPublishedOn * 1000);
  const candidate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), lastPublished.getHours(), lastPublished.getMinutes(), 0, 0);
  const dayDelta = (lastPublished.getDay() - candidate.getDay() + DAYS_IN_WEEK) % DAYS_IN_WEEK;
  candidate.setDate(candidate.getDate() + dayDelta);
  if (candidate <= now) {
    candidate.setDate(candidate.getDate() + DAYS_IN_WEEK);
  }
  return candidate;
};

/** Formats a date as `Wed, July 22 · 11:30 AM`. */
export const formatIdealPostTime = (date: Date): string => {
  const dayPart = date.toLocaleDateString('en-US', {weekday: 'short'});
  const datePart = date.toLocaleDateString('en-US', {month: 'long', day: 'numeric'});
  const timePart = date.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'});
  return `${dayPart}, ${datePart} · ${timePart}`;
};

/** True when the social wizard was opened from a fake-door post card ("Post again"). */
export const wasWizardOpenedFromFakeDoorPost = (): boolean => {
  return isFakeDoorWizardSession;
};

/** True when this AI chat page load originated from a fake-door prompt pill. */
export const wasAiChatOpenedFromFakeDoor = (): boolean => {
  return isFakeDoorAiChatSession;
};

/**
 * Caches the post's next ideal repost time so the social wizard opens in scheduling mode
 * prefilled with it. Posts that never published keep the wizard's publish-now default.
 */
export const prefillWizardScheduleForPost = (post: MarketingPlannerPost): void => {
  if (post.lastPublishedOn === null) {
    return;
  }
  setPrefilledSchedulingDate(getNextIdealPostTime(post.lastPublishedOn));
};

/** Fire-and-forget: call sites `void` this — failures are logged here so feedback never breaks the surrounding UI. */
export const recordMarketingPlannerFeedback = async (feedback: MarketingPlannerFeedback): Promise<void> => {
  try {
    await window.PMW.writeLocal('marketingplanner/recordFeedback', {
      ...feedback,
      negativeFeedbackPreset: feedback.negativeFeedbackPreset ?? null,
      comment: feedback.comment ?? null,
    });
  } catch (error) {
    console.error('Failed to record marketing planner feedback', error);
  }
};

export const updateMarketingPlannerState = async (state: MarketingPlannerState): Promise<void> => {
  await window.PMW.writeLocal('marketingplanner/updateState', {state});
};

/**
 * Duplicates the recommended social media post as a draft — its captions and designs come along —
 * and returns the wizard URL for the duplicate. The `source` param marks the wizard session as
 * fake-door so publish tracking fires.
 */
export const getSocialWizardUrlForDuplicatedPost = async (post: MarketingPlannerPost): Promise<string> => {
  const response = (await window.PMW.writeLocal('socialmedia/duplicatePostAsDraft', {postId: post.postId})) as DuplicatePostAsDraftResponse;
  const params = new URLSearchParams({source: FAKE_DOOR_WIZARD_SOURCE});
  return `${window.PMW.util.site_url(`socialmedia/post/${response.idPost}`)}?${params.toString()}`;
};

export const getAiChatUrlForPill = (pill: MarketingPlannerPromptPill): string => {
  const params = new URLSearchParams({prompt: pill.prompt, source: AiChatSource.MY_STUFF_MARKETING_PLANNER});
  return percentEncodeQuerySpaces(`${window.PMW.util.site_url('ai/chat')}?${params.toString()}`);
};

/** The server only sets these props for users in the fake-door cohort while the experiment flag is on. */
export const getMarketingPlannerPropsFromPage = (): MarketingPlannerProps | null => {
  return (window.reactPageProps.marketingPlannerProps as MarketingPlannerProps | null | undefined) ?? null;
};

/** True only on the root My Designs view — extra hash params after `#/designs` mean a folder view. */
export const isMarketingPlannerPageView = (pageHash: string, fullPageHash: string): boolean => {
  return pageHash === MY_STUFF_HASHES.myDesigns && fullPageHash.replace(MY_STUFF_HASHES.myDesigns, '') === '';
};

/**
 * Writes the new marketing-planner state into the server-rendered page props so an SPA
 * hash re-navigation that re-mounts the planner from `window.reactPageProps` picks up the
 * latest state instead of the stale value baked into the initial page load.
 */
export const syncServerPropsState = (state: MarketingPlannerState): void => {
  const marketingPlannerProps = getMarketingPlannerPropsFromPage();
  if (!marketingPlannerProps) {
    return;
  }
  marketingPlannerProps.state = state;
};

/** Fires FAKEDOOR_POST_USED once per page load when the visit originated from a fake-door prompt pill's AI Chat link. */
export const trackFakeDoorPostUsedIfNeeded = (): void => {
  if (!isFakeDoorAiChatSession || hasTrackedFakeDoorPostUsed) {
    return;
  }
  hasTrackedFakeDoorPostUsed = true;
  trackGA4CustomEvent(GA4EventName.FAKEDOOR_POST_USED);
};

/** Fires FAKEDOOR_EVERGREEN_POST_PUBLISHED once per page load when the wizard was opened from a fake-door post card. */
export const trackFakeDoorPostPublishedIfNeeded = (): void => {
  if (!isFakeDoorWizardSession || hasTrackedFakeDoorPostPublished) {
    return;
  }
  hasTrackedFakeDoorPostPublished = true;
  trackGA4CustomEvent(GA4EventName.FAKEDOOR_EVERGREEN_POST_PUBLISHED);
};
